import { AxiosError } from 'axios'
import { useMemo, useCallback, useContext, useState, useEffect, useRef, Suspense, lazy, DragEvent } from 'react'
import { useMatch, Link } from 'react-router-dom'
import { ErrorsContext } from '../contexts/errors_context'
import { Image, Item, Listing, Tag, Unit } from '../types'
import { unitsRepository } from '../repositories/units_repository'
import { itemsRepository } from '../repositories/items_repository'
import { ModalWrapper } from './modal_wrapper'
import { GenericNotFound } from './generic_not_found'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { SettingsContext } from '../contexts/settings_context'
import { ListingSingle } from './listing_single'
import Select from 'react-select'
import { conditions } from '../lib/conditions'
import { useTranslation } from 'react-i18next'

const nameForLanguage = (language: string, nameEn?: string, nameJa?: string) => {
  const preferJa = language.startsWith('ja')
  const preferred = preferJa ? nameJa : nameEn
  if (preferred && preferred !== '') return preferred
  return nameEn || nameJa || ''
}

import {
  LineShareButton,
  TwitterShareButton,
} from 'react-share'

import '../stylesheets/Sell.scss'
import { listingsRepository } from '../repositories/listings_repository'
import clsx from 'clsx'
import { unitImagesRepository } from '../repositories/unit_images_repository'
import { AuthContext } from '../contexts/auth_context'
import { sortImagesByOrdinality } from '../lib/sort_images'

type ListingHolicForm = Readonly<{
  id?: number
  platform: string
  priceJpy: number
  title?: string
}>

type UnitForm = Readonly<{
  condition: number
  description: string
}>

const DirtyModal = ({ children }) => {
  const { t } = useTranslation()

  return <ModalWrapper>
    <h1>{ t('listings.create.dirty.title') }</h1>
    <p>{ t('listings.create.dirty.body') }</p>
    { children }
  </ModalWrapper>
}

type SavedModalProps = Readonly<{
  active: boolean
  children: any
}>

const SavedModal = ({ active, children }: SavedModalProps) => {
  const { t } = useTranslation()

  return <ModalWrapper>
    <h1>{ t('listings.create.saved.title') }</h1>
    {
      active
        ? <>
          <p>{ t('listings.create.saved.update_incomplete') }</p>
          <p>{ t('listings.create.saved.update_hint', { action: t('listings.create.submit_update') }) }</p>
        </>
        : <>
          <p>{ t('listings.create.saved.create_incomplete') }</p>
          <p>{ t('listings.create.saved.create_hint', { action: t('listings.create.submit_new') }) }</p>
        </>
    }
    { children }
  </ModalWrapper>
}

type ListingPriceEntryProps = Readonly<{
  makeDirty: (dirty: boolean) => void
  callback: (listing: Listing, s?: string) => void
  item: Item
  listing: Listing
  loading: boolean
  tag?: Tag
}>

const ListingPriceEntry = ({ callback, makeDirty, item, loading, listing, tag }: ListingPriceEntryProps) => {
  const { t } = useTranslation()

  const {
    register,
    control,
    handleSubmit,
  } = useForm<ListingHolicForm>({
    defaultValues: {
      ...listing,
      priceJpy: listing.priceJpy < 1 ? undefined : listing.priceJpy,
    },
  })

  const priceJpy = useWatch({
    control,
    name: 'priceJpy',
  })

  useEffect(() => {
    const dirty = listing.priceJpy !== priceJpy

    makeDirty(dirty)
  }, [listing.priceJpy, makeDirty, priceJpy])

  const [showSavedScreen, setShowSavedScreen] = useState<boolean>(false)

  const onSubmit = useCallback((params) => {
    const listing: Listing = {
      ...params,
    }

    makeDirty(false)

    if (showSavedScreen) {
      // saved from desktop
      callback(listing, 'price')
    } else {
      // saved from mobile
      callback(listing)
    }
  }, [callback, makeDirty, showSavedScreen])

  const feeRate = 0.064
  const minFee = 50

  const fee = useMemo(() => {
    if (!priceJpy) return 0

    const f = Math.ceil(priceJpy * feeRate)
    return f < minFee ? minFee : f
  }, [priceJpy])

  const priceLessFee = useMemo(() => {
    if (!priceJpy || priceJpy < fee) {
      return 0
    }

    return priceJpy - fee
  }, [fee, priceJpy])

  return <form onSubmit={ handleSubmit(onSubmit) } className='full-height'>
    {
      !showSavedScreen
        ? null
        : <SavedModal active={ !!listing.id }>
          <p>
            <button className='full' onClick={ _ => setShowSavedScreen(false) }>{ t('listings.create.saved.ok') }</button>
          </p>
        </SavedModal>
    }
    <h1 className='hide-mobile'>{ t('listings.create.price.title') }</h1>
    <p className='match-title hide-desktop'>
      <strong>{ item.nameJa }</strong>
      {
        tag
          ? <span>{ tag.nameJa }</span>
          : null
      }
    </p>
    <ul className='organised'>
      <li>
        <label>{ t('listings.create.price.set_price') }</label>
        <input className='half' type='number' { ...register('priceJpy') } min={ 0 } max={ 999999999 } />
      </li>
    </ul>
    <div className='bottom-element'>
      <strong>{ t('listings.create.price.when_purchased') }</strong>
      <ul className='editable'>
        <li>
          <div>
            <span>{ t('listings.create.price.fee') }</span>
          </div>
          <span>{ t('listings.price.price_with_currency_symbol', { price: fee.toLocaleString() }) }</span>
        </li>
        { /* <li>
          <div>
            <strong>新規ユーザーキャンペーン！</strong><br />
            販売利益が1万円に達するまで販売手数料はかかりません。<a>詳しく見る</a>
          </div>
        </li> */ }
        <li>
          <span>{ t('listings.create.price.profit') }</span>
          <span>{ t('listings.price.price_with_currency_symbol', { price: priceLessFee.toLocaleString() }) }</span>
        </li>
      </ul>
      <p className='hide-mobile'>
        <button
          className={ clsx('full', { white: priceJpy === listing.priceJpy }) }
          disabled={ !priceJpy || priceJpy < 1 || loading }
          onClick={ _ => { setShowSavedScreen(true) } }
        >
          { t('listings.create.price.set_to', { price: t('listings.price.price_with_currency_symbol', { price: Number(priceJpy ?? 0).toLocaleString() }) }) }
        </button>
      </p>
      <p className='hide-desktop'>
        <button className={ clsx('full') } disabled={ !priceJpy || priceJpy < 1 || loading } onClick={ _ => { setShowSavedScreen(false) } }>{ t('listings.create.price.set_to', { price: t('listings.price.price_with_currency_symbol', { price: Number(priceJpy ?? 0).toLocaleString() }) }) }</button>
      </p>
    </div>
  </form>
}

type ListingDescriptionProps = Readonly<{
  callback: (unit: Unit, s?: string) => void
  loading: boolean
  isDirty: boolean
  unit: Unit
  listing: Listing
  makeDirty: (dirty: boolean) => void
}>

const ListingDescription = ({ callback, loading, isDirty, makeDirty, unit, listing }: ListingDescriptionProps) => {
  const { t } = useTranslation()

  const [showSavedScreen, setShowSavedScreen] = useState<boolean>(false)

  const {
    register,
    control,
    handleSubmit,
    getValues,
  } = useForm<UnitForm>({
    defaultValues: unit,
  })

  const condition = useWatch({
    control,
    name: 'condition',
  })

  const description = useWatch({
    control,
    name: 'description',
  })

  const onSubmit = useCallback((params) => {
    const ui: Unit = {
      ...unit,
      condition: params.condition,
      description: params.description,
    }

    makeDirty(false)

    if (showSavedScreen) {
      // saved from desktop
      callback(ui, 'description')
    } else {
      // saved from mobile
      callback(ui)
    }
  }, [callback, makeDirty, showSavedScreen, unit])

  const conditionsForForm = useMemo(() => {
    const c = conditions.filter(c => c.displayDefault).map(c => {
      return {
        value: c.id,
        label: t(`collections.conditions.long.${c.id}`),
      }
    })

    return c
  }, [t])

  const [showDirtyScreen, setShowDirtyScreen] = useState<boolean>(false)

  const goBack = useCallback(() => {
    if (!isDirty) {
      callback(unit)
      return
    }

    setShowDirtyScreen(true)
  }, [callback, isDirty, unit])

  const unDirty = useCallback(() => {
    makeDirty(false)

    callback(unit)

    setShowDirtyScreen(false)
  }, [callback, makeDirty, unit])

  useEffect(() => {
    const dirty = (
      condition !== unit.condition ||
      description !== unit.description
    )

    makeDirty(dirty)
  }, [condition, description, makeDirty, unit.condition, unit.description])

  return <>
    <form onSubmit={ handleSubmit(onSubmit) } className='full-height'>
      {
        !showSavedScreen
          ? null
          : <SavedModal active={ !!listing.id }>
            <p>
              <button className='full' onClick={ _ => setShowSavedScreen(false) }>{ t('listings.create.saved.ok') }</button>
            </p>
          </SavedModal>
      }
      {
        !showDirtyScreen
          ? null
          : <DirtyModal>
            <p>
              <button className='full white' onClick={ unDirty }>{ t('listings.create.dirty.discard') }</button>
            </p>
            <p>
              <button className='full' onClick={ _ => setShowDirtyScreen(false) }>{ t('listings.create.dirty.continue') }</button>
            </p>
          </DirtyModal>
      }
      <h1>{ t('listings.create.description.title') }</h1>
      <div className='select'>
        <label>{ t('listings.create.description.condition') }</label>
        <Controller
          control={ control }
          name='condition'
          render={ ({ field }) => (
            <Select
              options={ conditionsForForm }
              onChange={ val => field.onChange(val?.value) }
              placeholder={ t('defaults.select') }
              value={ conditionsForForm.find(s => s.value === getValues('condition')) }
            />
          ) }
        />
      </div>
      <p>
        <label>{ t('listings.create.description.description') }</label>
        <textarea { ...register('description') } rows={ 8 } />
      </p>
      <div className='bottom-element'>
        <p className='hide-mobile'>
          <button className={ clsx('full', { white: !isDirty }) } disabled={ loading } onClick={ _ => { setShowSavedScreen(true) } }>{ t('listings.create.description.save') }</button>
        </p>
        <p className='hide-desktop'>
          <button className={ clsx('full', { white: !isDirty }) } disabled={ loading } onClick={ _ => { setShowSavedScreen(false) } }>{ t('listings.create.description.save') }</button>
        </p>
        <p className='hide-desktop'>
          <button className='full white' disabled={ loading } onClick={ e => { e.preventDefault(); goBack() } }>{ t('listings.create.description.back') }</button>
        </p>
      </div>
    </form>
  </>
}

type ListingImagesProps = Readonly<{
  callback: (unit: Unit, s?: string) => void
  loading: boolean
  unit: Unit
}>

const ListingImages = ({ callback, unit }: ListingImagesProps) => {
  const { t } = useTranslation()
  const ImageUploadModal = lazy(() => import('./image_upload_modal').then(module => ({ default: module.ImageUploadModal })))

  const [images, setImages] = useState<Image[]>(() => sortImagesByOrdinality(unit?.images ?? []))
  const imagesRef = useRef(images)
  imagesRef.current = images

  const goBack = useCallback(() => {
    const newUnit: Unit = {
      ...unit,
      images,
    }

    callback(newUnit, undefined)
  }, [callback, images, unit])

  const afterImageUploaded = useCallback((im: Image) => {
    const next = [...imagesRef.current, im]
    imagesRef.current = next
    setImages(next)
    callback({ ...unit, images: next }, 'images')
  }, [callback, unit])

  const onClose = useCallback(() => {
  }, [])

  const { addError } = useContext(ErrorsContext)

  const [loadingDelete, setLoadingDelete] = useState<boolean>(false)
  const [imageToDelete, setImageToDelete] = useState<Image|undefined>()

  const persistInFlightRef = useRef(false)
  const lastRequestedOrderRef = useRef<string>('')

  const orderKey = (imageList: Image[]) => imageList.map(im => im.id).join(',')

  const flushPersistedOrder = useCallback(() => {
    if (!unit.id) return
    if (persistInFlightRef.current) return

    const current = imagesRef.current
    const requestedKey = orderKey(current)
    if (requestedKey === lastRequestedOrderRef.current) return

    persistInFlightRef.current = true
    const requestedIds = current.map(im => im.id)
    lastRequestedOrderRef.current = requestedKey

    unitImagesRepository
      .batchUpdateOrdinalities(unit.id, requestedIds)
      .catch((err: AxiosError) => {
        addError?.(err)
      })
      .finally(() => {
        persistInFlightRef.current = false
        if (orderKey(imagesRef.current) !== lastRequestedOrderRef.current) {
          flushPersistedOrder()
        }
      })
  }, [addError, unit.id])

  const persistOrder = useCallback((next: Image[]) => {
    const ordered = next.map((im, index) => ({ ...im, ordinality: index }))
    imagesRef.current = ordered
    setImages(ordered)
    callback({ ...unit, images: ordered }, 'images')
    flushPersistedOrder()
  }, [callback, flushPersistedOrder, unit])

  const reverseImages = useCallback(() => {
    if (imagesRef.current.length < 2) return

    persistOrder([...imagesRef.current].reverse())
  }, [persistOrder])

  const moveImage = useCallback((index: number, direction: -1 | 1) => {
    const target = index + direction
    const current = imagesRef.current
    if (target < 0 || target >= current.length) return

    const next = [...current]
    const [removed] = next.splice(index, 1)
    if (!removed) return

    next.splice(target, 0, removed)
    persistOrder(next)
  }, [persistOrder])

  const onDragStart = useCallback((e: DragEvent<HTMLDivElement>, index: number) => {
    e.dataTransfer.setData('text/plain', String(index))
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback((e: DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault()
    const from = Number(e.dataTransfer.getData('text/plain'))
    if (Number.isNaN(from) || from === dropIndex) return

    const next = [...imagesRef.current]
    const [removed] = next.splice(from, 1)
    if (!removed) return

    next.splice(dropIndex, 0, removed)
    persistOrder(next)
  }, [persistOrder])

  const deleteImage = useCallback((imageId: number) => {
    if (loadingDelete) return
    if (!unit?.id) return

    setImageToDelete(undefined)
    setLoadingDelete(true)

    unitImagesRepository
      .delete(unit.id, imageId)
      .then(() => {
        const next = imagesRef.current.filter(im => im.id !== imageId)
        imagesRef.current = next
        setImages(next)
        callback({ ...unit, images: next }, 'images')

        setImageToDelete(undefined)
      })
      .catch((err: AxiosError) => {
        addError?.(err)
      })
      .finally(() => {
        setLoadingDelete(false)
      })
  }, [addError, callback, loadingDelete, unit])

  const imagesCarousel = useMemo(() => {
    if (!images || images.length === 0) return

    return <div className='selling-images'>
      {
        images.map((image, index) => {
          return <div
            key={ image.id }
            className='image'
            draggable={ true }
            onDragStart={ e => onDragStart(e, index) }
            onDragOver={ onDragOver }
            onDrop={ e => onDrop(e, index) }
          >
            {
              index === 0
                ? <span className='main-badge'>{ t('listings.create.images.main') }</span>
                : null
            }
            <img src={ image.thumb2x } />
            <div className='reorder-tools'>
              <button
                type='button'
                className='reorder no-effect'
                aria-label={ t('listings.create.images.move_left') }
                disabled={ index === 0 }
                onClick={ _ => moveImage(index, -1) }
              >
                ←
              </button>
              <button
                type='button'
                className='reorder no-effect'
                aria-label={ t('listings.create.images.move_right') }
                disabled={ index === images.length - 1 }
                onClick={ _ => moveImage(index, 1) }
              >
                →
              </button>
            </div>
            <button className='tool' onClick={ _ => setImageToDelete(image) }>{ t('listings.create.images.delete') }</button>
          </div>
        })
      }
    </div>
  }, [images, moveImage, onDragOver, onDragStart, onDrop, t])

  return <div className='full-height'>
    {
      !imageToDelete
        ? null
        : <ModalWrapper>
          <h1>{ t('listings.create.images.delete_title') }</h1>
          <p>{ t('listings.create.images.delete_confirm') }</p>
          <img className='image-to-delete' src={ imageToDelete.thumb2x } />
          <p>
            <button className='full white' onClick={ _ => deleteImage(imageToDelete.id) } disabled={ loadingDelete }>{ t('listings.create.images.delete_submit') }</button>
          </p>
          <p>
            <button className='full' onClick={ _ => setImageToDelete(undefined) } disabled={ loadingDelete }>{ t('listings.create.images.cancel') }</button>
          </p>
        </ModalWrapper>
    }
    <h1>{ t('listings.create.images.title') }</h1>
    {
      images.length < 2
        ? null
        : <p>
          <button type='button' className='white no-effect' onClick={ reverseImages }>{ t('listings.create.images.reverse') }</button>
        </p>
    }
    {
      images.length > 0
        ? imagesCarousel
        : null
    }
    <Suspense fallback={<div>{ t('app.loading') }</div>}>
      <ImageUploadModal unit={ unit } onClose={ onClose } callback={ afterImageUploaded } showCancelButton={ false } />
    </Suspense>
    <div className='bottom-element hide-desktop'>
      <p>
        <button className='full white' onClick={ goBack }>{ t('listings.create.description.back') }</button>
      </p>
    </div>
  </div>
}

type ListingPreviewProps = Readonly<{
  changeScreenCallback: (screen: string) => void
  showConfirmScreenCallback: () => void
  showCancelModalCallback: () => void
  item: Item
  loading: boolean
  listing: Listing
  unit: Unit
}>

const ListingPreview = ({ changeScreenCallback, showConfirmScreenCallback, showCancelModalCallback, item, listing, unit }: ListingPreviewProps) => {
  const { t } = useTranslation()

  const setScreen = useCallback((screen: string) => {
    changeScreenCallback(screen)
  }, [changeScreenCallback])

  return <>
    <p className='top-left'>
      <button className='small white' onClick={ _ => setScreen('price') }>{ t('listings.create.preview.edit_price') }</button>
      {
        listing.id
          ? <button className='small top-right white' onClick={ showCancelModalCallback }>{ t('listings.create.preview.cancel_listing') }</button>
          : null
      }
    </p>
    <div className='preview-area hide-desktop'>
      <ul className='listings single'>
        <ListingSingle items={ [item] } unit={ unit } editing={ false } />
      </ul>
    </div>
    <div className='bottom-element'>
      <p>
        <button className='full white' onClick={ _ => setScreen('images') }>{ t('listings.create.preview.edit_images') }</button>
      </p>
      <p>
        <button className='full white' onClick={ _ => setScreen('description') }>{ t('listings.create.preview.edit_description') }</button>
      </p>
      <p>
        <button className='full' onClick={ showConfirmScreenCallback }>
          {
            listing.id
              ? t('listings.create.submit_update')
              : t('listings.create.submit_new')
          }
        </button>
      </p>
      {
        <div className='desktop'>
          <hr />
          <p>
            <strong>{ t('listings.create.preview.listed') }</strong>
          </p>
          <p>
            <button className='small white' onClick={ showCancelModalCallback }>{ t('listings.create.preview.cancel_listing') }</button>
          </p>
        </div>
      }
    </div>
  </>
}

type ListingCancelledProps = Readonly<{
  unit: Unit
}>

const ListingCancelled = ({ unit }: ListingCancelledProps) => {
  const { t } = useTranslation()

  return <div className='full-height'>
    <h1>{ t('listings.cancel_modal.title') }</h1>
    <p>{ t('listings.cancel_modal.body') }</p>
    <div className='bottom-element'>
      <p>
        <Link to={ `/u/${unit.userId ?? ''}/units/${unit.id ?? ''}` } className={ clsx('button full') }>{ t('listings.cancel_modal.link1') }</Link>
      </p>
      <p>
        <Link to={ `/u/${unit.userId ?? ''}` } className={ clsx('button full white') }>{ t('listings.cancel_modal.link2') }</Link>
      </p>
    </div>
  </div>
}

type ListingFinishedProps = Readonly<{
  unit: Unit
  item: Item
  listing: Listing
  isNew: boolean
  series?: Tag
}>

const ListingFinished = ({ item, listing, isNew, unit, series }: ListingFinishedProps) => {
  const { t, i18n } = useTranslation()

  const shareButtonProps = useCallback((snsName: string) => {
    const seriesName = series ? nameForLanguage(i18n.language, series.nameEn, series.nameJa) : ''
    const seriesText = seriesName ? `(${seriesName})` : ''

    return {
      className: `share share-${snsName} tool`,
      resetButtonStyle: false,
      quote: t('listings.create.share_quote', {
        name: nameForLanguage(i18n.language, item.nameEn, item.nameJa),
        series: seriesText,
        price: listing.priceJpy,
      }),
      url: `https://holic.net/u/${unit.userId ?? 0}/units/${unit.id ?? 0}`,
    }
  }, [i18n.language, item.nameEn, item.nameJa, listing.priceJpy, series, t, unit.id, unit.userId])

  return <div className='full-height'>
    <h1>{
      isNew
        ? t('listings.create.finished.title_new')
        : t('listings.create.finished.title_updated')
    }</h1>
    <div className='information'>
      <h2>{ t('listings.create.finished.listed_in') }</h2>
      <div>
        <strong>{ t('listings.create.finished.market_title') }</strong>
        <p>{ t('listings.create.finished.market_body') }</p>
      </div>
      <div>
        <strong>{ t('listings.create.finished.shop_title') }</strong>
        <p>{ t('listings.create.finished.shop_body') }</p>
      </div>
    </div>
    <div className='bottom-element'>
      <p className='multiple-buttons'>
        <LineShareButton { ...shareButtonProps('line') } className='full white'>
          { t('modal.share_collection.line') }
        </LineShareButton>
        <TwitterShareButton { ...shareButtonProps('twitter') } className='full white'
          hashtags={ [
            t('collections.share.twitter_hashtag1'),
            t('collections.share.twitter_hashtag2')
          ] }>
          { t('modal.share_collection.twitter') }
        </TwitterShareButton>
      </p>
      <p>
        <Link to={ `/u/${unit.userId ?? 'me'}` } className='button full'>{ t('listings.create.finished.view_shop') }</Link>
      </p>
      { /* <p>
        <Link to={ '/match' } className='button full'>もう一回マッチする</Link>
      </p> */ }
    </div>
  </div>
}

type ListingWrapperProps = Readonly<{
  changeScreenCallback: (screen: string) => void
  showConfirmScreenCallback: () => void
  makeDirty: (dirty: boolean) => void
  unit: Unit
  item: Item
  listing: Listing
  tag?: Tag
  children: any
  screen: string
  isDirty: boolean
}>

const ListingWrapper = ({ changeScreenCallback, showConfirmScreenCallback, makeDirty, isDirty, unit, item, tag, listing, children, screen }: ListingWrapperProps) => {
  const { t } = useTranslation()
  const [showDirtyScreen, setShowDirtyScreen] = useState<string|undefined>()

  const nav = useCallback((s: string) => {
    if (isDirty) {
      setShowDirtyScreen(s)
      return
    }

    changeScreenCallback(s)
  }, [changeScreenCallback, isDirty])

  const unDirty = useCallback(() => {
    if (!showDirtyScreen) return

    if (showDirtyScreen === 'finished') {
      // ready to list
      showConfirmScreenCallback()
    } else {
      makeDirty(false)
      changeScreenCallback(showDirtyScreen)
    }

    // just in case
    setShowDirtyScreen(undefined)
  }, [changeScreenCallback, makeDirty, showConfirmScreenCallback, showDirtyScreen])

  const confirmList = useCallback(() => {
    if (isDirty) {
      setShowDirtyScreen('finished')
      return
    }

    showConfirmScreenCallback()
  }, [isDirty, showConfirmScreenCallback])

  return <>
    {
      !showDirtyScreen
        ? null
        : <DirtyModal>
          <p>
            <button className='full white' onClick={ unDirty }>{ t('listings.create.dirty.discard') }</button>
          </p>
          <p>
            <button className='full' onClick={ _ => setShowDirtyScreen(undefined) }>{ t('listings.create.dirty.continue') }</button>
          </p>
        </DirtyModal>
    }
    <div className='slim hide-mobile'>
      <p className='match-title'>
        <strong>{ item.nameJa }</strong>
        {
          tag
            ? <span>{ tag.nameJa }</span>
            : null
        }
      </p>
    </div>
    <div className='slim'>
      <div className='left hide-mobile skinny preview-area'>
        <ul className='listings single'>
          <ListingSingle items={ [item] } unit={ unit } editing={ false } />
        </ul>
        {
          listing.priceJpy > 0
            ? <>
              <div className='checklist'>
                <button
                  className={ clsx({ done: unit.images && unit.images.length > 0 }) }
                  onClick={ _ => nav('images') }
                  disabled={ screen === 'images' }
                >
                  { t('listings.create.preview.edit_images') }
                </button>
                <button
                  className={ clsx({ done: unit.condition > 0 }) }
                  onClick={ _ => nav('description') }
                  disabled={ screen === 'description' }
                >
                  { t('listings.create.preview.edit_description') }
                </button>
                <button
                  className={ clsx({ done: listing.priceJpy > 0 }) }
                  onClick={ _ => nav('price') }
                  disabled={ screen === 'price' }
                >
                  { t('listings.create.preview.edit_price') }
                </button>
              </div>
              {
                screen === 'finished'
                  ? null
                  : <p>
                    <button
                      className={ clsx('full', { white: !(unit.images && unit.images.length > 0) || !unit.condition }) }
                      onClick={ confirmList }
                    >
                      {
                        listing.id
                          ? t('listings.create.submit_update')
                          : t('listings.create.submit_new')
                      }
                    </button>
                  </p>
              }
            </>
            : null
        }
      </div>
      <div className='right fat full-height'>
        { children }
      </div>
    </div>
  </>
}

const ListingCreate = () => {
  const { t } = useTranslation()
  const { setMinimalNavigation } = useContext(SettingsContext)
  const { addError } = useContext(ErrorsContext)

  useEffect(() => {
    if (!setMinimalNavigation) return

    setMinimalNavigation(true)

    return () => {
      setMinimalNavigation(false)
    }
  }, [setMinimalNavigation])

  const [loading, setLoading] = useState<boolean>(true)
  const [loadingSubscreen, setLoadingSubscreen] = useState<boolean>(false)

  const { currentUser } = useContext(AuthContext)

  const [item, setItem] = useState<Item|undefined>()
  const [unit, setUnit] = useState<Unit|undefined>()
  const [listing, setListing] = useState<Listing|undefined>()

  const paramsListing = useMatch('/items/:item_id/listing')?.params
  const paramsUnit = useMatch('/items/:item_id/unit/:unit_id/listing')?.params

  const itemId = useMemo<number|undefined>(() => {
    const id = parseInt(paramsListing?.item_id ?? '')
    if (isNaN(id)) {
      const id2 = parseInt(paramsUnit?.item_id ?? '')
      return isNaN(id2) ? undefined : id2
    }

    return id
  }, [paramsListing?.item_id, paramsUnit?.item_id])

  const unitId = useMemo<number|undefined>(() => {
    const id = parseInt(paramsUnit?.unit_id ?? '')
    return isNaN(id) ? undefined : id
  }, [paramsUnit])

  const getUnit = useCallback((id: number) => {
    unitsRepository
      .get(id)
      .then(({ unit }) => {
        setUnit(unit)

        if (unit.items && unit.items.length > 0) {
          setItem(unit.items[0])
        }
      })
      .catch((err: AxiosError) => {
        addError?.(err)
        setLoading(false)
      })
  }, [addError])

  const getItem = useCallback((id: number) => {
    itemsRepository
      .get(id)
      .then(({ item }) => {
        setItem(item)
      })
      .catch((err: AxiosError) => {
        addError?.(err)
        setLoading(false)
      })
  }, [addError])

  useEffect(() => {
    if (!unitId) return

    getUnit(unitId)
  }, [getUnit, unitId])

  useEffect(() => {
    // getUnit will include item anyway
    if (unitId) return

    if (!itemId) return

    getItem(itemId)
  }, [getItem, itemId, unitId])

  useEffect(() => {
    if (!unit) return

    if (listing) return

    const holicListing = unit.listings?.filter(l => l.platform === 'holic') ?? []

    if (holicListing.length > 0) {
      setListing(holicListing[0])
      return
    }

    // create a new listing
    const newListing: Listing = {
      platform: 'holic',
      priceJpy: 0,
    }

    setListing(newListing)
  }, [listing, unit])

  const setNewUnit = useCallback((unit?: Unit) => {
    const items = unit?.items
      ? unit.items
      : item
        ? [item]
        : []

    const newUnit: Unit = {
      ...unit,
      condition: unit?.condition ?? 0,
      verified: unit?.verified ?? false,
      items: items,
    }

    if (unit?.id) {
      // get user item (for images etc)
      setLoading(true)
      getUnit(unit.id)
    }

    setUnit(newUnit)
  }, [getUnit, item])

  useEffect(() => {
    if (!item) return
    if (unit ?? unitId) return

    if (item.units && item.units.length > 0) {
      // allow user to choose which unit
      setLoading(false)
      return
    }

    // make a new one
    setNewUnit()
  }, [item, setNewUnit, unit, unitId])

  useEffect(() => {
    if (item && unit && listing) {
      setLoading(false)
    }
  }, [item, listing, unit])

  const listingPriceEntryCallback = useCallback((listing: Listing, s?: string) => {
    if (!unit || !item) return

    if (!unit.id) {
      // create the new unit here

      if (loadingSubscreen) return

      setLoadingSubscreen(true)

      unitsRepository
        .create({
          ...unit,
          itemIds: [item.id],
        })
        .then(({ unit }) => {
          setUnit(unit)

          setListing(listing)
          setScreen(s)
        })
        .catch((err: AxiosError) => {
          addError?.(err)
        })
        .finally(() => {
          setLoadingSubscreen(false)
        })
    } else {
      setListing(listing)
      setScreen(s)
    }
  }, [addError, item, loadingSubscreen, unit])

  const descriptionCallback = useCallback((ui: Unit, s?: string) => {
    if (!unit?.id) return

    // if (loadingSubscreen) return

    // setLoadingSubscreen(true)

    setUnit(ui)
    setScreen(s)

    // unitsRepository
    //   .update({
    //     id: unit.id,
    //     params: {
    //       description: ui.description ?? '',
    //       condition: ui.condition,
    //     },
    //   })
    //   .then(() => {
    //     setUnit(ui)
    //     setScreen(undefined)
    //   })
    //   .catch((err: AxiosError) => {
    //     addError?.(err)
    //   })
    //   .finally(() => {
    //     setLoadingSubscreen(false)
    //   })
  }, [unit?.id])

  const imagesCallback = useCallback((ui: Unit, s?: string) => {
    setUnit(ui)
    setScreen(s)
  }, [])

  const [screen, setScreen] = useState<string|undefined>()

  const changeScreenCallback = useCallback((screen: string) => {
    setScreen(screen)
  }, [])

  const createOrUpdateListing = useCallback((unitId: number) => {
    if (!listing) return

    if (listing.id) {
      // update listing
      listingsRepository
        .update({
          id: listing.id,
          params: listing,
        })
        .then(() => {
          // done
          setScreen('updated')
        })
        .catch((err: AxiosError) => {
          addError?.(err)
        })
        .finally(() => {
          setLoadingSubscreen(false)
        })
    } else {
      // create listing
      listingsRepository
        .create({
          ...listing,
          unitId: unitId,
        })
        .then(({ listing }) => {
          // done
          setListing(listing)
          setScreen('created')
        })
        .catch((err: AxiosError) => {
          addError?.(err)
        })
        .finally(() => {
          setLoadingSubscreen(false)
        })
    }
  }, [addError, listing])

  const [showConfirmScreen, setShowConfirmScreen] = useState<boolean>(false)
  const showConfirmScreenCallback = useCallback(() => {
    setShowConfirmScreen(true)
  }, [])

  const [showCancelModal, setShowCancelModal] = useState<boolean>(false)
  const showCancelModalCallback = useCallback(() => {
    setShowCancelModal(true)
  }, [])

  const confirmSaleCallback = useCallback(() => {
    if (!unit?.id) return

    if (loadingSubscreen) return

    setLoadingSubscreen(true)

    // update unit
    unitsRepository
      .update({
        id: unit.id,
        params: {
          description: unit.description ?? '',
          condition: unit.condition,
        },
      })
      .then(() => {
        // create or update listing
        createOrUpdateListing(unit.id ?? 0)
      })
      .catch((err: AxiosError) => {
        addError?.(err)
      })
      .finally(() => {
        setLoadingSubscreen(false)
        setShowConfirmScreen(false)
      })
  }, [addError, createOrUpdateListing, loadingSubscreen, unit])

  const series = useMemo(() => {
    if (!item) return

    return item.tags.length > 0 ? item.tags.reduce((prev, next) => prev.depth > next.depth ? prev : next) : undefined
  }, [item])

  const [isDirty, setIsDirty] = useState<boolean>(false)
  const makeDirty = useCallback((dirty: boolean) => {
    setIsDirty(dirty)
  }, [])

  const condition = useMemo(() => {
    return unit && unit.condition > 0
      ? t('listings.create.condition_rank', { rank: t(`collections.conditions.short.${unit.condition}`) })
      : t('collections.conditions.short.0')
  }, [t, unit])

  const unitForPreview = useMemo(() => {
    if (!listing || !unit) return unit

    const ui: Unit = {
      ...unit,
      listings: [listing],
    }

    return ui
  }, [listing, unit])

  const confirmCancelListing = useCallback(() => {
    if (!listing?.id) return

    if (loadingSubscreen) return

    setLoadingSubscreen(true)

    listingsRepository
      .destroy(listing.id)
      .then(() => {
        // done
        setScreen('cancelled')
        setShowCancelModal(false)
      })
      .catch((err: AxiosError) => {
        addError?.(err)
      })
      .finally(() => {
        setLoadingSubscreen(false)
      })
  }, [addError, listing, loadingSubscreen])

  const listingScreen = useMemo(() => {
    if (!unit || !listing || !item || !unitForPreview) return

    if (unit.public === false) return

    const screenLabel = screen === 'created' || screen === 'updated' ? 'finished' : screen ?? ''

    return <ListingWrapper
      screen={ screenLabel }
      unit={ unitForPreview }
      item={ item }
      listing={ listing }
      isDirty={ isDirty }
      makeDirty={ makeDirty }
      tag={ series }
      changeScreenCallback={ changeScreenCallback }
      showConfirmScreenCallback={ showConfirmScreenCallback }
    >
      {
        showCancelModal
          ? <ModalWrapper>
            <h1>{ t('listings.create.cancel_confirm.title') }</h1>
            <p>{ t('listings.create.cancel_confirm.body') }</p>
            <p>
              <button className='full white' onClick={ confirmCancelListing } disabled={ loadingSubscreen }>{ t('listings.create.cancel_confirm.confirm') }</button>
            </p>
            <p>
              <button className='full' onClick={ _ => {
                setShowCancelModal(false)
              } } disabled={ loadingSubscreen }>{ t('listings.create.cancel_confirm.continue') }</button>
            </p>
          </ModalWrapper>
          : null
      }
      {
        !showConfirmScreen
          ? null
          : <ModalWrapper>
            <h1>{ t('listings.create.confirm.title') }</h1>
            <ul className='editable'>
              <li>
                <span>{ t('listings.create.confirm.card') }</span>
                <span>{ item.nameJa }</span>
              </li>
              <li>
                <span>{ t('listings.create.confirm.condition') }</span>
                <span>{ condition }</span>
              </li>
              <li>
                <span>{ t('listings.create.confirm.price') }</span>
                <span>{ t('listings.price.price_with_currency_symbol', { price: Number(listing.priceJpy).toLocaleString() }) }</span>
              </li>
            </ul>
            <p>
              <button className='full white' onClick={ _ => setShowConfirmScreen(false) } disabled={ loading }>{ t('listings.create.confirm.edit') }</button>
            </p>
            <p>
              <button className='full' onClick={ confirmSaleCallback } disabled={ loading }>
                {
                  listing.id
                    ? t('listings.create.submit_update')
                    : t('listings.create.submit_new')
                }
              </button>
            </p>
          </ModalWrapper>
      }
      {
        screenLabel === 'finished'
          ? <ListingFinished unit={ unit } isNew={ screen === 'created' } item={ item } listing={ listing } series={ series } />
          : screenLabel === 'cancelled'
            ? <ListingCancelled unit={ unit } />
            : screenLabel === 'description'
              ? <ListingDescription unit={ unit } listing={ listing } isDirty={ isDirty } callback={ descriptionCallback } loading={ loadingSubscreen } makeDirty={ makeDirty } />
              : screenLabel === 'images'
                ? <ListingImages unit={ unit } callback={ imagesCallback } loading={ loadingSubscreen } />
                : screenLabel === 'price' || !listing.priceJpy || listing.priceJpy < 1
                  ? <ListingPriceEntry item={ item } listing={ listing } callback={ listingPriceEntryCallback } tag={ series } loading={ loadingSubscreen } makeDirty={ makeDirty } />
                  : <ListingPreview listing={ listing } item={ item } unit={ unitForPreview } changeScreenCallback={ changeScreenCallback } showConfirmScreenCallback={ showConfirmScreenCallback } showCancelModalCallback={ showCancelModalCallback } loading={ loadingSubscreen } />
      }
    </ListingWrapper>
  }, [changeScreenCallback, condition, confirmCancelListing, confirmSaleCallback, descriptionCallback, imagesCallback, isDirty, item, listing, listingPriceEntryCallback, loading, loadingSubscreen, makeDirty, screen, series, showCancelModal, showCancelModalCallback, showConfirmScreen, showConfirmScreenCallback, t, unit, unitForPreview])

  if (loading) {
    return <p>
      { t('app.loading') }
    </p>
  }

  if (!unit) {
    if (item?.units) {
      if (!currentUser) return <></>

      return <ModalWrapper>
        <h1>{ t('listings.create.choose_unit.title') }</h1>
        <p>{ t('listings.create.choose_unit.body') }</p>
        <ul className='editable'>
          {
            item.units.map((unit) => {
              const condition = unit.condition > 0
                ? t('listings.create.condition_rank', { rank: t(`collections.conditions.short.${unit.condition}`) })
                : t('collections.conditions.short.0')

              return <li key={ unit.id }>
                <span>
                  <Link target='_blank' to={ `/u/${currentUser.id}/units/${unit.id ?? 0}` }>{ item.nameJa }</Link> { condition }
                </span>
                <button className='tool' onClick={ _ => setNewUnit(unit) }>{ t('listings.create.choose_unit.select') }</button>
              </li>
            })
          }
        </ul>
        <button className='full' onClick={ _ => setNewUnit() }>{ t('listings.create.choose_unit.add_new') }</button>
      </ModalWrapper>
    }
  }

  if (item?.id !== itemId) {
    return <GenericNotFound />
  }

  if (listingScreen) {
    return listingScreen
  }

  return <GenericNotFound />
}

export { ListingCreate }
